import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Beef, User, LogOut, Sparkles, X, QrCode, ShoppingBag, Clock, Phone, MapPin, MessageCircle, Plus, Minus, ChevronRight, Package, CheckCircle, Truck, AlertCircle, Calendar, Bell } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PointsAnimation from '@/components/PointsAnimation';
import PushNotificationManager from '@/components/PushNotificationManager';
import { useCart } from '@/context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Order status mapping
const orderStatusConfig = {
  pending: { label: 'In Attesa', color: '#F59E0B', icon: Clock, description: 'Il tuo ordine è stato ricevuto' },
  confirmed: { label: 'Confermato', color: '#3B82F6', icon: CheckCircle, description: 'Ordine confermato, in preparazione' },
  ready: { label: 'Pronto!', color: '#10B981', icon: Package, description: 'Il tuo ordine è pronto!' },
  completed: { label: 'Completato', color: '#6B7280', icon: CheckCircle, description: 'Ordine completato' },
  cancelled: { label: 'Annullato', color: '#EF4444', icon: AlertCircle, description: 'Ordine annullato' }
};

export default function CustomerDashboard({ user, onLogout }) {
  const [card, setCard] = useState(null);
  const [offers, setOffers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [newPoints, setNewPoints] = useState(0);
  const [activeTab, setActiveTab] = useState('card');
  const [selectedOrderCategory, setSelectedOrderCategory] = useState(null);
  const { cart: ctxCart, addToCart: ctxAddToCart, updateQuantity: ctxUpdateQuantity, cartTotal } = useCart();
  const [showNotificationAlert, setShowNotificationAlert] = useState(false);
  const previousPointsRef = useRef(null);
  const previousOrdersRef = useRef([]);

  useEffect(() => {
    fetchData();

    const refreshAccountData = () => {
      if (document.visibilityState !== 'visible') return;
      checkForNewPoints();
      checkForOrderUpdates();
    };

    const refreshAllCustomerData = () => {
      if (document.visibilityState !== 'visible') return;
      refreshAccountData();
      refreshPublicContent();
    };
    
    // Account data stays live; public content is refreshed without reloading the page.
    const accountInterval = window.setInterval(refreshAccountData, 10000);
    const contentInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshPublicContent();
    }, 15000);

    window.addEventListener('focus', refreshAllCustomerData);
    window.addEventListener('online', refreshAllCustomerData);
    document.addEventListener('visibilitychange', refreshAllCustomerData);
    
    // Mostra avviso notifiche dopo 2 secondi se non attivate
    const notifTimer = setTimeout(() => {
      if ('Notification' in window && Notification.permission === 'default') {
        setShowNotificationAlert(true);
      }
    }, 2000);
    
    return () => {
      window.clearInterval(accountInterval);
      window.clearInterval(contentInterval);
      window.removeEventListener('focus', refreshAllCustomerData);
      window.removeEventListener('online', refreshAllCustomerData);
      document.removeEventListener('visibilitychange', refreshAllCustomerData);
      clearTimeout(notifTimer);
    };
  }, []);

  const checkForNewPoints = async () => {
    try {
      const [cardResponse, notificationsResponse] = await Promise.all([
        axios.get(`${API}/cards/my-card`, getAuthHeaders()),
        axios.get(`${API}/notifications`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      const currentPoints = cardResponse.data.current_points;

      setNotifications(notificationsResponse.data || []);
      
      if (previousPointsRef.current !== null && currentPoints > previousPointsRef.current) {
        const earned = currentPoints - previousPointsRef.current;
        setNewPoints(earned);
        setShowPointsAnimation(true);
      }
      
      setCard(cardResponse.data);
      previousPointsRef.current = currentPoints;
    } catch (error) {
      console.error('Error checking points:', error);
    }
  };
  
  const checkForOrderUpdates = async () => {
    try {
      const response = await axios.get(`${API}/my-orders`, getAuthHeaders());
      const newOrders = response.data || [];
      
      // Controlla se qualche ordine è diventato "ready"
      newOrders.forEach(newOrder => {
        const prevOrder = previousOrdersRef.current.find(o => o.id === newOrder.id);
        if (prevOrder && prevOrder.status !== 'ready' && newOrder.status === 'ready') {
          // Ordine appena diventato pronto!
          addNotification({
            type: 'order_ready',
            title: 'Ordine Pronto! 🥩',
            message: 'Il tuo ordine è pronto per il ritiro!',
            orderId: newOrder.id
          });
          
          // Vibra il telefono se supportato
          if (navigator.vibrate) {
            navigator.vibrate([200, 100, 200]);
          }
          
          // Vai alla tab ordini
          setActiveTab('orders');
        }
      });
      
      setMyOrders(newOrders);
      previousOrdersRef.current = newOrders;
    } catch (error) {
      console.error('Error checking orders:', error);
    }
  };
  
  const addNotification = (notif) => {
    const newNotif = {
      id: Date.now(),
      ...notif,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev].slice(0, 10)); // Max 10 notifiche
  };
  
  const clearNotification = async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (typeof id === 'string') {
      try {
        await axios.post(`${API}/notifications/${id}/read`, {}, getAuthHeaders());
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const refreshPublicContent = async () => {
    try {
      const timestamp = Date.now();
      const requestConfig = {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      };
      const [offersRes, settingsRes, productsRes] = await Promise.all([
        axios.get(`${API}/offers?t=${timestamp}`, requestConfig),
        axios.get(`${API}/settings?t=${timestamp}`, requestConfig),
        axios.get(`${API}/products?t=${timestamp}`, requestConfig)
      ]);

      setOffers(offersRes.data.filter(o => o.active));
      setSettings(settingsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error refreshing customer content:', error);
    }
  };

  const fetchData = async () => {
    try {
      const timestamp = Date.now();
      const publicRequestConfig = {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      };
      const [cardRes, offersRes, settingsRes, productsRes, ordersRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/cards/my-card`, getAuthHeaders()),
        axios.get(`${API}/offers?t=${timestamp}`, publicRequestConfig),
        axios.get(`${API}/settings?t=${timestamp}`, publicRequestConfig),
        axios.get(`${API}/products?t=${timestamp}`, publicRequestConfig),
        axios.get(`${API}/my-orders`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      
      setCard(cardRes.data);
      setOffers(offersRes.data.filter(o => o.active));
      setSettings(settingsRes.data);
      setProducts(productsRes.data);
      setMyOrders(ordersRes.data || []);
      setNotifications(notificationsRes.data || []);
      previousOrdersRef.current = ordersRes.data || [];
      
      // Store initial points for comparison
      previousPointsRef.current = cardRes.data.current_points;
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };
  
  // Cart functions (backed by shared, persistent CartContext)
  // Map product_id -> quantity for easy lookup in the JSX below
  const cart = {};
  ctxCart.forEach(item => { cart[item.product_id] = item.quantity; });

  const addToCart = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const step = product.unit === 'pz' ? 1 : (product.min_quantity || 1);
    ctxAddToCart(product, step);
  };

  const removeFromCart = (productId) => {
    const product = products.find(p => p.id === productId);
    const item = ctxCart.find(i => i.product_id === productId);
    if (!product || !item) return;
    const step = product.unit === 'pz' ? 1 : (product.min_quantity || 1);
    ctxUpdateQuantity(productId, item.quantity - step, product.min_quantity || 0.1);
  };

  const getCartTotal = () => cartTotal;

  const getCartItemsCount = () => ctxCart.length;
  
  // Dynamic colors
  const primaryColor = settings?.primary_color || '#9B1B30';
  const secondaryColor = settings?.secondary_color || '#D4AF37';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-xl" style={{ color: primaryColor }}>Caricamento...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'card', label: 'La Mia Card', icon: QrCode },
    { id: 'orders', label: 'I Miei Ordini', icon: Package, badge: myOrders.filter(o => o.status === 'ready').length },
    { id: 'order', label: 'Ordina', icon: ShoppingBag },
    { id: 'offers', label: 'Offerte', icon: Sparkles },
    { id: 'info', label: 'Info Negozio', icon: MapPin }
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="customer-dashboard">
      {/* Points Animation */}
      {showPointsAnimation && (
        <PointsAnimation 
          points={newPoints}
          totalPoints={Number(card?.current_points || 0)}
          threshold={Number(settings?.points_for_discount || 10)}
          discountValue={Number(settings?.discount_value || 5)}
          onComplete={() => setShowPointsAnimation(false)} 
        />
      )}
      
      {/* Notification Alert Banner - Attiva Notifiche */}
      {showNotificationAlert && 'Notification' in window && Notification.permission === 'default' && (
        <div className="fixed top-0 left-0 right-0 z-50 p-4 text-white animate-pulse" style={{ backgroundColor: primaryColor }}>
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold">Attiva le Notifiche!</p>
                <p className="text-sm opacity-90">Ricevi avvisi quando il tuo ordine è pronto</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  const permission = await Notification.requestPermission();
                  if (permission === 'granted') {
                    toast.success('Notifiche attivate!');
                  }
                  setShowNotificationAlert(false);
                }}
                className="px-4 py-2 bg-white rounded-full font-bold text-sm"
                style={{ color: primaryColor }}
              >
                Attiva
              </button>
              <button
                onClick={() => setShowNotificationAlert(false)}
                className="p-2 hover:bg-white/20 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* In-App Notifications Toast */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
          {notifications.filter(n => !n.read).slice(0, 3).map(notif => (
            <div 
              key={notif.id}
              className="bg-white rounded-xl shadow-2xl p-4 border-l-4 animate-slide-in"
              style={{ borderColor: notif.type === 'order_ready' ? '#10B981' : primaryColor }}
            >
              <div className="flex items-start gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: notif.type === 'order_ready' ? '#10B98120' : `${primaryColor}20` }}
                >
                  {notif.type === 'order_ready' ? (
                    <Package className="w-5 h-5 text-green-500" />
                  ) : (
                    <Sparkles className="w-5 h-5" style={{ color: primaryColor }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{notif.title}</p>
                  <p className="text-sm text-gray-600">{notif.message}</p>
                </div>
                <button 
                  onClick={() => clearNotification(notif.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Header */}
      <header className={`bg-white border-b border-[#E5E5E5] sticky z-40 ${showNotificationAlert ? 'top-[72px]' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Beef className="w-7 h-7" style={{ color: primaryColor }} />
            <h1 className="text-xl font-serif font-bold" style={{ color: primaryColor }}>
              {settings?.business_name || 'Macelleria Borgese'}
            </h1>
          </Link>
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            {notifications.filter(n => !n.read).length > 0 && (
              <div className="relative">
                <Bell className="w-6 h-6 text-gray-500" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {notifications.filter(n => !n.read).length}
                </span>
              </div>
            )}
            <Link to="/profile" className="text-[#5A5A5A] hover:opacity-70" data-testid="profile-link">
              <User size={22} />
            </Link>
            <button onClick={onLogout} className="text-[#5A5A5A] hover:opacity-70" data-testid="logout-btn">
              <LogOut size={22} />
            </button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto px-4 pb-2">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'order') setSelectedOrderCategory(null);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={activeTab === tab.id ? { backgroundColor: primaryColor } : {}}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.id === 'order' && getCartItemsCount() > 0 && (
                  <span 
                    className="ml-1 text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: secondaryColor, color: '#2C2C2C' }}
                  >
                    {getCartItemsCount()}
                  </span>
                )}
                {tab.id === 'orders' && tab.badge > 0 && (
                  <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-green-500 text-white animate-pulse">
                    {tab.badge} pronto!
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Welcome Banner - Always visible */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-serif font-bold" style={{ color: primaryColor }}>
              Ciao, {user.full_name}!
            </h2>
            <p className="text-[#5A5A5A] text-sm">
              {activeTab === 'card' && 'Ecco la tua Fidelity Card'}
              {activeTab === 'orders' && 'Traccia i tuoi ordini'}
              {activeTab === 'order' && 'Ordina e ritira in negozio'}
              {activeTab === 'offers' && 'Le nostre offerte speciali'}
              {activeTab === 'info' && 'Orari e contatti'}
            </p>
          </div>
          {card && (
            <div 
              className="text-right px-4 py-2 rounded-xl"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <p className="text-xs text-gray-500">I tuoi punti</p>
              <p className="text-2xl font-bold" style={{ color: primaryColor }}>{card.current_points}</p>
            </div>
          )}
        </div>

        {/* ============ TAB: LA MIA CARD ============ */}
        {activeTab === 'card' && (
          <>
            {/* Fidelity Card */}
            {card && (
              <div 
                className="rounded-2xl p-6 text-white card-shadow relative overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow mb-6"
                onClick={() => setShowQRModal(true)}
                data-testid="fidelity-card"
                style={{
                  background: settings?.card_background_image 
                    ? `linear-gradient(135deg, ${settings?.card_bg_color || primaryColor}dd, ${settings?.card_bg_gradient_end || '#3A0F15'}dd), url(${settings.card_background_image})`
                    : `linear-gradient(135deg, ${settings?.card_bg_color || primaryColor}, ${settings?.card_bg_gradient_end || '#3A0F15'})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: settings?.card_text_color || '#FFFFFF'
                }}
              >
                <div 
                  className="absolute top-0 right-0 w-64 h-64 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10"
                  style={{ backgroundColor: settings?.card_accent_color || secondaryColor }}
                ></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Beef className="w-10 h-10" style={{ color: settings?.card_accent_color || secondaryColor }} />
                      <div>
                        <p className="text-xs mb-1" style={{ color: settings?.card_accent_color || secondaryColor }}>Fidelity Card</p>
                        <h3 className="text-lg font-serif font-bold">{user.full_name}</h3>
                      </div>
                    </div>
                    <div className="bg-white p-2 rounded-lg">
                      <img 
                        src={`data:image/png;base64,${card.qr_code_data}`} 
                        alt="QR Code" 
                        className="w-12 h-12"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-xs mb-1 opacity-70">Punti Attuali</p>
                      <p className="text-4xl font-mono font-bold" style={{ color: settings?.card_accent_color || secondaryColor }}>{card.current_points}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs mb-1 opacity-70">Totale</p>
                      <p className="text-xl font-mono font-bold">{card.total_points_earned}</p>
                    </div>
                  </div>
                  
                  {settings?.card_expiry_message && (
                    <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-2">
                      <p className="text-xs flex items-center gap-2">
                        <Clock size={12} style={{ color: settings?.card_accent_color || secondaryColor }} />
                        {settings.card_expiry_message}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-3 text-center">
                    <p className="text-xs flex items-center justify-center gap-1" style={{ color: settings?.card_accent_color || secondaryColor }}>
                      <QrCode size={14} />
                      Tocca per ingrandire il QR Code
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => setActiveTab('order')}
                className="bg-white rounded-xl p-4 card-shadow flex items-center gap-3 hover:shadow-lg transition-shadow"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <ShoppingBag className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm" style={{ color: primaryColor }}>Ordina Ora</p>
                  <p className="text-xs text-gray-500">Ritira in negozio</p>
                </div>
                <ChevronRight size={18} className="ml-auto text-gray-400" />
              </button>
              
              <button
                onClick={() => setActiveTab('offers')}
                className="bg-white rounded-xl p-4 card-shadow flex items-center gap-3 hover:shadow-lg transition-shadow"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${secondaryColor}25` }}>
                  <Sparkles className="w-5 h-5" style={{ color: secondaryColor }} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm" style={{ color: primaryColor }}>Offerte</p>
                  <p className="text-xs text-gray-500">{offers.length} attive</p>
                </div>
                <ChevronRight size={18} className="ml-auto text-gray-400" />
              </button>
            </div>

            {/* Points Info */}
            <div className="bg-white rounded-xl p-5 card-shadow">
              <h4 className="font-semibold mb-3" style={{ color: primaryColor }}>Come funziona</h4>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: primaryColor }}>1</div>
                  <p>Ogni €{settings?.euros_per_point || 10} spesi = 1 punto</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: primaryColor }}>2</div>
                  <p>Raggiungi {settings?.points_for_discount || 10} punti</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: primaryColor }}>3</div>
                  <p>Con {settings?.points_for_discount || 10} punti hai €{Number(settings?.discount_value || 5).toFixed(2)} di sconto da utilizzare in macelleria.</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ TAB: I MIEI ORDINI ============ */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {myOrders.length > 0 ? (
              <>
                {/* Ordini Pronti - In evidenza */}
                {myOrders.filter(o => o.status === 'ready').length > 0 && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-green-700">Ordini pronti!</h4>
                    </div>
                    {myOrders.filter(o => o.status === 'ready').map(order => (
                      <div key={order.id} className="bg-white rounded-xl p-4 mb-2 last:mb-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-green-700">
                              {order.fulfillment_type === 'delivery' ? 'Consegna' : 'Ritiro'}: {new Date(order.pickup_date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })} alle {order.pickup_time}
                            </p>
                            {order.fulfillment_type === 'delivery' && order.delivery_address && (
                              <p className="text-sm text-gray-600 mt-1">{order.delivery_address}</p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              {order.items?.length || 0} prodotti • €{order.total_amount?.toFixed(2)}
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
                            PRONTO!
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Lista tutti gli ordini */}
                <h4 className="font-serif font-bold text-lg" style={{ color: primaryColor }}>Storico Ordini</h4>
                {myOrders.map(order => {
                  const statusInfo = orderStatusConfig[order.status] || orderStatusConfig.pending;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <div key={order.id} className="bg-white rounded-xl p-4 card-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${statusInfo.color}20` }}
                          >
                            <StatusIcon className="w-5 h-5" style={{ color: statusInfo.color }} />
                          </div>
                          <div>
                            <p className="font-semibold text-sm" style={{ color: statusInfo.color }}>
                              {statusInfo.label}
                            </p>
                            <p className="text-xs text-gray-500">{statusInfo.description}</p>
                          </div>
                        </div>
                        <p className="text-lg font-bold" style={{ color: primaryColor }}>
                          €{order.total_amount?.toFixed(2)}
                        </p>
                      </div>
                      
                      {/* Dettagli ordine */}
                      <div className="border-t pt-3 mt-3">
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(order.pickup_date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {order.pickup_time}
                          </span>
                        </div>
                        
                        {/* Lista prodotti */}
                        <div className="space-y-1">
                          {order.items?.slice(0, 3).map((item, idx) => (
                            <p key={idx} className="text-sm text-gray-600">
                              • {item.product_name} x{item.quantity} {item.unit}
                            </p>
                          ))}
                          {order.items?.length > 3 && (
                            <p className="text-sm text-gray-400">
                              + altri {order.items.length - 3} prodotti
                            </p>
                          )}
                        </div>
                        
                        {/* Data ordine */}
                        <p className="text-xs text-gray-400 mt-2">
                          Ordinato il {new Date(order.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center card-shadow">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Non hai ancora effettuato ordini</p>
                <button
                  onClick={() => { setActiveTab('order'); setSelectedOrderCategory(null); }}
                  className="px-6 py-3 rounded-full text-white font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  Fai il tuo primo ordine
                </button>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: ORDINA ============ */}
        {activeTab === 'order' && (
          <div className="space-y-4">
            {products.length > 0 ? (
              <>
                {selectedOrderCategory === null ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'pollo', label: 'Pollo' },
                      { key: 'vitella', label: 'Vitella' },
                      { key: 'maiale', label: 'Maiale' },
                      { key: 'preparati', label: 'Preparati' },
                      { key: 'pane', label: 'Pane' },
                      { key: 'vino', label: 'Vino' },
                      { key: 'uova', label: 'Uova' },
                      { key: 'salame', label: 'Salame' }
                    ].map(({ key, label }) => {
                      const count = products.filter(p => p.category === key).length;
                      if (count === 0) return null;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSelectedOrderCategory(key)}
                          className="bg-white rounded-2xl p-5 card-shadow text-left min-h-[120px] flex flex-col justify-between"
                        >
                          <Beef className="w-8 h-8" style={{ color: primaryColor }} />
                          <div>
                            <h4 className="font-serif font-bold text-xl" style={{ color: primaryColor }}>{label}</h4>
                            <p className="text-sm text-gray-500">{count} {count === 1 ? 'prodotto' : 'prodotti'}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectedOrderCategory(null)}
                      className="inline-flex items-center gap-2 font-medium mb-2"
                      style={{ color: primaryColor }}
                    >
                      <ChevronRight className="w-5 h-5 rotate-180" />
                      Torna alle categorie
                    </button>

                    <h4 className="font-serif font-bold text-2xl mb-3" style={{ color: primaryColor }}>
                      {{ pollo: 'Pollo', vitella: 'Vitella', maiale: 'Maiale', preparati: 'Preparati', pane: 'Pane', vino: 'Vino', uova: 'Uova', salame: 'Salame' }[selectedOrderCategory] || selectedOrderCategory}
                    </h4>

                    <div className="space-y-2">
                      {products.filter(p => p.category === selectedOrderCategory).map(product => (
                        <div key={product.id} className="bg-white rounded-xl p-4 card-shadow flex items-center gap-4">
                          {product.image_url && (
                            <img src={product.image_url} alt={product.name} className="w-16 h-16 rounded-lg object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-sm truncate">{product.name}</h5>
                            <p className="text-xs text-gray-500 truncate">{product.description}</p>
                            <p className="font-bold text-sm mt-1" style={{ color: primaryColor }}>
                              €{(product.price_per_unit || product.price)?.toFixed(2)}/{product.unit || 'kg'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {cart[product.id] ? (
                              <>
                                <button onClick={() => removeFromCart(product.id)} className="w-8 h-8 rounded-full flex items-center justify-center border-2" style={{ borderColor: primaryColor, color: primaryColor }}>
                                  <Minus size={16} />
                                </button>
                                <span className="w-8 text-center font-bold">{cart[product.id]}</span>
                                <button onClick={() => addToCart(product.id)} className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                                  <Plus size={16} />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => addToCart(product.id)} className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ backgroundColor: primaryColor }}>
                                Aggiungi
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {getCartItemsCount() > 0 && (
                  <div className="fixed bottom-4 left-4 right-4 max-w-4xl mx-auto">
                    <Link to="/ordina" className="block text-white rounded-xl p-4 card-shadow flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                      <div className="flex items-center gap-3">
                        <ShoppingBag size={24} />
                        <div>
                          <p className="font-semibold">{getCartItemsCount()} prodotti</p>
                          <p className="text-sm opacity-80">Vai alla pagina ordini completa</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">€{getCartTotal().toFixed(2)}</p>
                        <ChevronRight size={20} className="inline" />
                      </div>
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center card-shadow">
                <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Nessun prodotto disponibile al momento</p>
                <Link to="/ordina" className="inline-block px-6 py-3 rounded-full text-white font-medium" style={{ backgroundColor: primaryColor }}>
                  Vai alla pagina ordini
                </Link>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: OFFERTE ============ */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            {offers.length > 0 ? (
              offers.map((offer) => (
                <div 
                  key={offer.id} 
                  className="relative rounded-2xl overflow-hidden card-shadow"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
                  data-testid={`offer-card-${offer.id}`}
                >
                  {offer.image_url && (
                    <div className="absolute inset-0 opacity-20">
                      <img src={offer.image_url} alt={offer.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-20 rounded-full -translate-y-1/2 translate-x-1/2" style={{ backgroundColor: secondaryColor }}></div>
                  
                  <div className="relative z-10 p-5">
                    {offer.image_url && (
                      <img src={offer.image_url} alt={offer.title} className="w-full h-40 object-cover rounded-lg mb-4" />
                    )}
                    <div className="flex gap-2 mb-3">
                      {offer.discount_percentage && (
                        <span className="px-3 py-1 rounded-full font-bold text-sm" style={{ backgroundColor: secondaryColor, color: '#2C2C2C' }}>
                          -{offer.discount_percentage}%
                        </span>
                      )}
                      {offer.price_per_kg && (
                        <span className="px-3 py-1 rounded-full font-bold text-sm bg-white/20 text-white">
                          €{offer.price_per_kg.toFixed(2)}/kg
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-serif font-bold text-white mb-2">{offer.title}</h3>
                    <p className="text-white/80 text-sm">{offer.description}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl p-8 text-center card-shadow">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessuna offerta disponibile al momento</p>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: INFO NEGOZIO ============ */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            {/* Orari */}
            <div className="bg-white rounded-xl p-5 card-shadow">
              <h4 className="font-serif font-bold text-lg mb-4 flex items-center gap-2" style={{ color: primaryColor }}>
                <Clock size={20} />
                Orari di Apertura
              </h4>
              <div className="space-y-2">
                {[
                  { day: 'Lunedì', key: 'mon' },
                  { day: 'Martedì', key: 'tue' },
                  { day: 'Mercoledì', key: 'wed' },
                  { day: 'Giovedì', key: 'thu' },
                  { day: 'Venerdì', key: 'fri' },
                  { day: 'Sabato', key: 'sat' },
                  { day: 'Domenica', key: 'sun' }
                ].map(({ day, key }) => {
                  const hours = settings?.opening_hours?.[key];
                  const isClosed = !hours?.open;
                  const hasAfternoon = hours?.afternoon_open;
                  
                  return (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="font-medium text-sm">{day}</span>
                      <span className={`text-sm ${isClosed ? 'text-red-500' : 'text-gray-600'}`}>
                        {isClosed ? 'Chiuso' : (
                          <>
                            {hours.open}-{hours.close}
                            {hasAfternoon && ` / ${hours.afternoon_open}-${hours.afternoon_close}`}
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contatti */}
            <div className="bg-white rounded-xl p-5 card-shadow">
              <h4 className="font-serif font-bold text-lg mb-4 flex items-center gap-2" style={{ color: primaryColor }}>
                <Phone size={20} />
                Contatti
              </h4>
              <div className="space-y-3">
                {settings?.phone && (
                  <a href={`tel:${settings.phone}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone size={18} style={{ color: primaryColor }} />
                    <div>
                      <p className="text-xs text-gray-500">Telefono</p>
                      <p className="font-semibold text-sm">{settings.phone}</p>
                    </div>
                  </a>
                )}
                {settings?.whatsapp && (
                  <a 
                    href={`https://wa.me/${settings.whatsapp?.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-green-50 rounded-lg"
                  >
                    <MessageCircle size={18} className="text-green-600" />
                    <div>
                      <p className="text-xs text-gray-500">WhatsApp</p>
                      <p className="font-semibold text-sm text-green-600">{settings.whatsapp}</p>
                    </div>
                  </a>
                )}
                {settings?.email && (
                  <a href={`mailto:${settings.email}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span style={{ color: primaryColor }}>✉️</span>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-semibold text-sm">{settings.email}</p>
                    </div>
                  </a>
                )}
              </div>
            </div>

            {/* Indirizzo */}
            <div className="bg-white rounded-xl p-5 card-shadow">
              <h4 className="font-serif font-bold text-lg mb-4 flex items-center gap-2" style={{ color: primaryColor }}>
                <MapPin size={20} />
                Dove Siamo
              </h4>
              <p className="text-gray-600 mb-4">{settings?.address || 'Via Roma 123, 00100 Roma'}</p>
              {settings?.maps_url && (
                <a 
                  href={settings.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  <MapPin size={16} />
                  Apri in Google Maps
                </a>
              )}
              <div className="mt-4 rounded-xl overflow-hidden h-40 bg-gray-200">
                <iframe
                  title="Mappa"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(settings?.address || 'Via Roma 123 Roma')}&output=embed&z=15`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* QR Code Full Screen Modal */}
      {showQRModal && card && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center p-6"
          onClick={() => setShowQRModal(false)}
          data-testid="qr-modal"
        >
          <button
            onClick={() => setShowQRModal(false)}
            className="absolute top-6 right-6 text-white transition-colors"
            style={{ hover: { color: secondaryColor } }}
            data-testid="close-qr-modal"
          >
            <X size={40} />
          </button>

          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Beef className="w-10 h-10" style={{ color: secondaryColor }} />
                <h2 className="text-2xl font-serif font-bold text-white">
                  {settings?.business_name || 'Macelleria Borgese'}
                </h2>
              </div>
              <p className="text-lg font-semibold" style={{ color: secondaryColor }}>{user.full_name}</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-2xl inline-block mb-8">
              <img 
                src={`data:image/png;base64,${card.qr_code_data}`} 
                alt="QR Code" 
                className="w-64 h-64 sm:w-80 sm:h-80"
                data-testid="qr-code-fullscreen"
              />
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 inline-block">
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-sm mb-1" style={{ color: secondaryColor }}>Punti Attuali</p>
                  <p className="text-4xl font-bold text-white">{card.current_points}</p>
                </div>
                <div className="h-12 w-px bg-white/20"></div>
                <div className="text-center">
                  <p className="text-white/70 text-sm mb-1">Totale</p>
                  <p className="text-2xl font-bold text-white">{card.total_points_earned}</p>
                </div>
              </div>
            </div>

            <p className="text-white/80 text-sm max-w-xs mx-auto">
              Mostra questo codice QR al cassiere per registrare i tuoi acquisti e guadagnare punti
            </p>

            {settings?.card_expiry_message && (
              <div className="mt-4 bg-orange-500/20 backdrop-blur-sm rounded-lg px-4 py-2 inline-block">
                <p className="text-orange-400 text-sm flex items-center gap-2">
                  <Clock size={16} />
                  {settings.card_expiry_message}
                </p>
              </div>
            )}

            <p className="text-white/40 text-xs mt-8">
              Tocca ovunque per chiudere
            </p>
          </div>
        </div>
      )}

      {/* Push Notification Manager */}
      <PushNotificationManager user={user} primaryColor={primaryColor} />
    </div>
  );
}
